/**
 * FinancingExpiriesPage — Finanzierungs-Ausläufe (Jul-2026, User-Wunsch).
 * Bank-Bestandslisten (Santander Kredit + Renault Bank) — getrennt von
 * Leasing-Rücknahmen. Leads werden standardmäßig MANUELL pro Vorgang angelegt
 * („Als Lead übernehmen" + Verkäufer-Zuweisung); umschaltbar auf Automatik
 * mit konfigurierbarem Zeitraum. Abarbeitung über das Lead-Modul
 * (Telefonliste, Wiedervorlage, Ergebnis).
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Landmark, RefreshCw, Upload, Loader2, CheckCircle2, UserPlus, CalendarClock, Search, ThumbsUp, ThumbsDown, PhoneMissed, Settings, Trash2 } from "lucide-react";
import { toast } from "sonner";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { SellerSelect } from "../components/leads/SellerSelect";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import FinRunoutSettingsDialog from "../components/dealer/FinRunoutSettingsDialog";
import { BankImportCard } from "../components/leasing/BankImportCard";
import { useGearsVisible } from "../lib/gearVisibility";
import InfoHint from "../components/listing/InfoHint";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso + "T00:00:00").toLocaleDateString("de-DE"); } catch { return iso; }
};
const fmtEur = (v) => (v || v === 0 ? Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2 }) + " €" : "—");
const daysUntil = (iso) => Math.ceil((new Date(iso + "T00:00:00") - new Date()) / 86400000);

const BANK_LOGOS = { santander: "/banks/santander.svg", renault: "/banks/renault-bank.svg", ca: "/banks/ca-auto-bank.png", bank11: "/banks/bank11.svg?v=2" };
export function BankLogo({ bank, className = "h-6" }) {
    const lc = (bank || "").toLowerCase();
    const key = lc.includes("bank11") || lc.includes("bank 11") ? "bank11" : lc.includes("renault") ? "renault" : (lc.includes("auto bank") || lc.startsWith("ca")) ? "ca" : "santander";
    return <img src={BANK_LOGOS[key]} alt={bank || "Santander"} title={bank || "Santander"} className={`${className} w-auto ${key === "ca" ? "bg-black rounded-sm p-0.5" : ""}`} />;
}

const LEAD_STATUS_LABELS = { assigned: "Offen", in_progress: "In Arbeit", won: "Gewonnen", lost: "Verloren", archived: "Archiv" };

export default function FinancingExpiriesPage() {
    const { user } = useAuth();
    const gearsOk = useGearsVisible();
    const navigate = useNavigate();
    const [bucket, setBucket] = useState("due");
    const [wishOnly, setWishOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem("tw_fin_page_size")) || 20);
    const [searchTerm, setSearchTerm] = useState("");
    const [search, setSearch] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [importOpen, setImportOpen] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [rowBusy, setRowBusy] = useState("");
    const [cfgBusy] = [false];
    // Aug-2026: windowDraft entfernt (Einstellungen im Zahnrad-Dialog).
    const [detailId, setDetailId] = useState(null);
    const [runoutOpen, setRunoutOpen] = useState(false);
    const [wishTaskBusy, setWishTaskBusy] = useState(false);

    // Jul-2026 (User-Wunsch) — Wunsch-Weiterleitung: KI-erkannten Kundenwunsch
    // per Klick als Aufgabe an den zuständigen Verkäufer übergeben.
    const createWishTask = async (cid) => {
        setWishTaskBusy(true);
        try {
            const r = await api.post(`/dealer/financing/contracts/${cid}/wish-task`);
            toast.success(`Aufgabe erstellt — zugewiesen an ${r.data?.assignee_name || "Verkäufer"}.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aufgabe konnte nicht erstellt werden.");
        } finally {
            setWishTaskBusy(false);
        }
    };

    const settings = data?.settings || { auto_leads: false, window_days: 60 };
    const windowDays = data?.lead_window_days || 60;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/financing/expiries", { params: { bucket, page, page_size: pageSize, search, wish_only: wishOnly ? 1 : 0 } });
            setData(r.data);
        } catch { setData(null); }
        setLoading(false);
    }, [bucket, page, pageSize, search, wishOnly]);

    // Suche mit kurzer Tipp-Pause (Debounce)
    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchTerm.trim()); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user) {
            load();
            api.get("/me/tasks/assignable-employees?context=sales").then((r) => setEmployees(r.data.items || [])).catch(() => {});
        }
    }, [user, navigate, load]);

    // Aug-2026: Bank-Import läuft jetzt über den konsolidierten BankImportCard-Dialog.

    // Aug-2026: Lead-Automatik-Einstellungen leben jetzt im Zahnrad-Dialog.

    // Aug-2026 (User-Wunsch): einzelnen Vorgang löschen (inkl. Auto-Lead).
    const deleteContract = async (c) => {
        if (!window.confirm(`Vorgang ${c.contract_number} (${c.customer?.name || "?"}) wirklich löschen?`)) return;
        setRowBusy(c.id);
        try {
            await api.delete(`/dealer/financing/contracts/${c.id}`);
            toast.success("Vorgang gelöscht");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
        } finally {
            setRowBusy(null);
        }
    };

    const createLead = async (c, sellerId = null) => {
        setRowBusy(c.id);
        try {
            const r = await api.post(`/dealer/financing/contracts/${c.id}/create-lead`, { seller_id: sellerId || null });
            toast.success(r.data.assigned_seller_name
                ? `Lead angelegt und ${r.data.assigned_seller_name} zugewiesen — erscheint in dessen Lead-Eingang.`
                : "Lead angelegt — jetzt im Lead-Modul abarbeiten.");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Lead konnte nicht angelegt werden"); }
        setRowBusy("");
    };

    // Anruf-Ergebnis Schnellerfassung (User-Wunsch Jul-2026): 1 Klick direkt in der Liste
    const quickResult = async (c, kind) => {
        setRowBusy(c.id);
        try {
            const now = new Date();
            const stamp = `${now.toLocaleDateString("de-DE")} ${now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
            const prev = c.lead?.notes ? `${c.lead.notes}\n` : "";
            let body;
            if (kind === "interest") body = { status: "in_progress", notes: `${prev}[${stamp}] Erreicht — Interesse` };
            else if (kind === "no_interest") body = { status: "lost", close_reason: "Kein Interesse (Telefonliste)", notes: `${prev}[${stamp}] Erreicht — kein Interesse` };
            else {
                const morgen = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
                body = { notes: `${prev}[${stamp}] Nicht erreicht`, follow_up_date: morgen };
            }
            const r = await api.patch(`/dealer/leads/${c.lead_id}/status`, body);
            toast.success(kind === "interest" ? "Ergebnis erfasst: Erreicht — Interesse"
                : kind === "no_interest" ? "Lead geschlossen: kein Interesse"
                : "Nicht erreicht notiert — Wiedervorlage morgen");
            setData((p) => ({
                ...p,
                items: p.items.map((x) => (x.id === c.id
                    ? { ...x, lead: { ...(x.lead || {}), status: r.data.lead.status, follow_up_date: r.data.lead.follow_up_date, notes: r.data.lead.notes } }
                    : x)),
            }));
        } catch (e) { toast.error(e?.response?.data?.detail || "Ergebnis konnte nicht gespeichert werden"); }
        setRowBusy("");
    };

    const reassignSeller = async (c, sellerId) => {
        setRowBusy(c.id);
        try {
            const r = await api.patch(`/dealer/leads/${c.lead_id}/assign`, { seller_id: sellerId || null });
            if (r.data.warning) toast.warning(r.data.warning);
            else toast.success(sellerId ? `Zugewiesen an ${r.data.assigned_seller_name}` : "Zuweisung aufgehoben");
            setData((p) => ({
                ...p,
                items: p.items.map((x) => (x.id === c.id
                    ? { ...x, lead: { ...(x.lead || {}), assigned_seller_id: r.data.assigned_seller_id, assigned_seller_name: r.data.assigned_seller_name } }
                    : x)),
            }));
        } catch (e) { toast.error(e?.response?.data?.detail || "Zuweisung fehlgeschlagen"); }
        setRowBusy("");
    };

    // Kurzinfo für den zugewiesenen Verkäufer (User-Wunsch Jul-2026)
    const saveAssignNote = async (c, note) => {
        try {
            await api.patch(`/dealer/leads/${c.lead_id}/assign`, { seller_id: c.lead?.assigned_seller_id, note });
            toast.success("Kurzinfo für Mitarbeiter gespeichert");
            setData((p) => ({
                ...p,
                items: p.items.map((x) => (x.id === c.id
                    ? { ...x, lead: { ...(x.lead || {}), assignment_note: note || null } }
                    : x)),
            }));
        } catch (e) { toast.error(e?.response?.data?.detail || "Kurzinfo konnte nicht gespeichert werden"); }
    };

    if (!user) return <div className="p-8">Lade…</div>;
    const items = data?.items || [];
    const counts = data?.counts || {};
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const changePageSize = (n) => {
        setPageSize(n);
        setPage(1);
        localStorage.setItem("tw_fin_page_size", String(n));
    };
    const BUCKETS = [
        { k: "due", l: `Fällig (≤ ${windowDays} Tage)` },
        { k: "future", l: "Zukünftig" },
        { k: "past", l: "Abgelaufen" },
        { k: "all", l: "Alle" },
    ];

    return (
        <div className="min-h-screen bg-gray-50" data-testid="financing-expiries-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2">
                                <Landmark className="w-4 h-4 text-[#FFCC00]" /> Anschlussgeschäft
                            </div>
                            <h1 className="font-display font-black text-4xl md:text-5xl inline-flex items-center gap-3 flex-wrap">
                                Finanzierungs-Ausläufe
                                {/* Jul-2026 (User-Wunsch) — Zahnrad rechts vom Titel: Kontaktstrecke */}
                                {gearsOk && <button type="button" onClick={() => setRunoutOpen(true)}
                                    title="Kontaktstrecke einstellen (Anschreiben-Zeitpunkte, Vorlagen, KI-Themen)"
                                    className="relative text-gray-400 hover:text-[#FFCC00] transition-colors align-middle"
                                    data-testid="fin-runout-settings-btn">
                                    <Settings className="w-7 h-7" />
                                    {settings.auto_emails && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400" title="Automatik aktiv" />}
                                </button>}
                            </h1>
                            <p className="text-sm text-gray-300 mt-1 max-w-2xl">
                                Bank-Bestände (<b>Santander</b> + <b>CA Auto Bank</b> + <b>Renault Bank</b> + <b>Bank11</b>) — getrennt von Leasing-Rücknahmen.
                                Vorgänge werden als <b>Leads</b> (Telefonliste mit Wiedervorlage &amp; Ergebnis) über das Lead-Modul abgearbeitet.
                            </p>
                        </div>
                        <div className="flex flex-col items-end gap-3 min-w-0 ml-auto">
                            {/* Zeile 1: Navigation */}
                            <div className="flex gap-2 flex-wrap items-center justify-end max-w-full" data-testid="fin-nav-row">
                                <Button onClick={() => navigate("/dashboard")} variant="outline" className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white rounded-sm text-xs font-bold uppercase tracking-wider" data-testid="back-to-dashboard">
                                    ← Dashboard
                                </Button>
                                <Link to="/dealer/leads" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="fin-link-leads">
                                    Leads →
                                </Link>
                                <Link to="/dealer/leasingrueckgabe" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="fin-link-leasing">
                                    Leasing-Rücknahmen →
                                </Link>
                            </div>
                            {/* Zeile 2: Aktionen */}
                            <div className="flex gap-2 flex-wrap items-center justify-end max-w-full" data-testid="fin-action-row">
                                <Button onClick={() => setImportOpen(true)}
                                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="fin-import-btn">
                                    <Upload className="w-4 h-4 mr-1.5" />
                                    Bank-Liste importieren
                                </Button>
                                <Button onClick={load} variant="outline" className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white rounded-sm" data-testid="fin-refresh-btn">
                                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                </Button>
                            </div>
                        </div>
                    </div>
                    {/* Bucket-Filter */}
                    <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="fin-bucket-filter">
                        {BUCKETS.map((b) => (
                            <button key={b.k} type="button" onClick={() => { setBucket(b.k); setPage(1); }}
                                className={`text-xs px-3 py-1.5 rounded-sm border font-bold uppercase tracking-wider ${bucket === b.k ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-transparent text-gray-300 border-gray-600 hover:border-[#FFCC00]"}`}
                                data-testid={`fin-bucket-${b.k}`}>
                                {b.l}{counts[b.k] !== undefined ? ` (${counts[b.k]})` : ""}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-4">
                {/* Aug-2026 (User-Wunsch): Lead-Automatik-Einstellungen ins Zahnrad
                    (FinRunoutSettingsDialog) verlegt — Leiste hier entfernt. */}

                {/* Seiten-Blöcke (10/20/50) + Blättern */}
                {(total > 0 || search || wishOnly) && (
                    <div className="flex items-center justify-between flex-wrap gap-2" data-testid="fin-pagination">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Suche: Name, Bank, Modell, Hersteller, Vertragsnr., FIN, Ort, Telefon …"
                                    className="pl-7 pr-2 py-1.5 w-72 max-w-full text-xs border border-gray-300 rounded-sm bg-white focus:border-[#111827] focus:outline-none"
                                    data-testid="fin-search-input" />
                            </div>
                            {/* Jul-2026 (User-Wunsch) — Wunsch-Filter für schnelle Abarbeitung */}
                            <button type="button" onClick={() => { setWishOnly((v) => !v); setPage(1); }}
                                className={`text-xs px-3 py-1.5 rounded-sm border font-bold uppercase tracking-wider ${wishOnly ? "bg-violet-600 text-white border-violet-600" : "bg-white text-violet-700 border-violet-300 hover:border-violet-600"}`}
                                title="Nur Vorgänge anzeigen, bei denen die KI aus einer Kundenantwort einen Wunsch erkannt hat"
                                data-testid="fin-wish-only-toggle">
                                {wishOnly ? "✓ " : ""}Nur mit Kundenwunsch
                            </button>
                            <div className="flex items-center gap-1 text-xs">
                                <span className="font-bold uppercase tracking-wider text-gray-500 mr-1">Pro Seite</span>
                            {[10, 20, 50].map((n) => (
                                <button key={n} type="button" onClick={() => changePageSize(n)}
                                    className={`px-2.5 py-1 rounded-sm border font-bold ${pageSize === n ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-[#111827]"}`}
                                    data-testid={`fin-page-size-${n}`}>
                                    {n}
                                </button>
                            ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
                                className="px-2.5 py-1 rounded-sm border border-gray-300 bg-white font-bold disabled:opacity-40 hover:border-[#111827]"
                                data-testid="fin-page-prev">← Zurück</button>
                            <span className="text-gray-600 font-mono-num" data-testid="fin-page-info">
                                Seite {page} / {totalPages} · {total.toLocaleString("de-DE")} Vorgänge
                            </span>
                            <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}
                                className="px-2.5 py-1 rounded-sm border border-gray-300 bg-white font-bold disabled:opacity-40 hover:border-[#111827]"
                                data-testid="fin-page-next">Weiter →</button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="p-10 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
                ) : items.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-sm text-gray-500" data-testid="fin-empty">
                        Keine Finanzierungs-Vorgänge in diesem Filter.
                        {counts.all === 0 && <div className="mt-2">Zum Start die Bank-Bestandsliste (.xls/.xlsx) oben importieren — Santander- und Renault-Format werden automatisch erkannt.</div>}
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="fin-table">
                        <table className="w-full text-sm min-w-[1100px]">
                            <thead className="bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">Bank</th>
                                    <th className="px-3 py-2 text-left">Vertragsende</th>
                                    <th className="px-3 py-2 text-left">Vertragsnr.</th>
                                    <th className="px-3 py-2 text-left">Kunde</th>
                                    <th className="px-3 py-2 text-left">Telefon</th>
                                    <th className="px-3 py-2 text-left">Fahrzeug</th>
                                    <th className="px-3 py-2 text-right">Rate</th>
                                    <th className="px-3 py-2 text-right">
                                        <span className="inline-flex items-center gap-1">Ballon / Saldo / Summe
                                            <InfoHint id="fin.balloon_col" defaultText={"BALLON = vertraglich vereinbarte Schlussrate (die letzte, große Rate am Ende eines Ballonkredits — der Betrag, um den es im Anschlussgeschäft geht).\nSALDO = noch offener Restbetrag laut Bank (Ablösewert, z. B. aus der Santander-Liste).\nSUMME = ursprüngliche Finanzierungssumme — wird gezeigt, wenn die Bank weder Schlussrate noch Saldo liefert (normale Ratenkredite: die letzte Rate entspricht der normalen Monatsrate in der Spalte „Rate\u201C)."} side="bottom" />
                                        </span>
                                    </th>
                                    <th className="px-3 py-2 text-left">Lead / Verkäufer</th>
                                    <th className="px-1 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((c) => {
                                    const d = daysUntil(c.end_date);
                                    const urgent = d >= 0 && d <= 30;
                                    const lead = c.lead;
                                    return (
                                        <tr key={c.id} onClick={() => setDetailId(c.id)}
                                            title="Klicken für Details"
                                            className="border-t border-gray-100 hover:bg-gray-50 align-top cursor-pointer"
                                            data-testid={`fin-row-${c.contract_number}`}>
                                            <td className="px-3 py-2">
                                                <div className="flex flex-col gap-1" data-testid={`fin-bank-${c.contract_number}`}>
                                                    <BankLogo bank={c.bank} className="h-6" />
                                                    {c.contract_type === "LEAS" ? (
                                                        <span className="inline-flex self-start text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-200">Leasing</span>
                                                    ) : Number(c.balloon) > 0 ? (
                                                        <span className="inline-flex self-start text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200" title="Ballonkredit — hohe Schlussrate" data-testid={`fin-type-${c.contract_number}`}>Ballonkredit</span>
                                                    ) : (
                                                        <span className="inline-flex self-start text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-gray-50 text-gray-600 border border-gray-200" title="Normale Finanzierung — gleichbleibende Rate" data-testid={`fin-type-${c.contract_number}`}>Ratenkredit</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`font-mono-num font-bold ${urgent ? "text-red-600" : d >= 0 && d <= windowDays ? "text-amber-600" : "text-gray-800"}`}>
                                                    {fmtDate(c.end_date)}
                                                </span>
                                                {d >= 0 && d <= windowDays && <span className="ml-1.5 text-[10px] text-gray-500">in {d} Tg.</span>}
                                            </td>
                                            <td className="px-3 py-2 font-mono-num text-xs">{c.contract_number}</td>
                                            <td className="px-3 py-2 font-medium">{c.customer?.name || "—"}
                                                <div className="text-[11px] text-gray-500">{c.customer?.plz} {c.customer?.city}</div>
                                                {c.customer_wish?.label && (
                                                    <div className="mt-0.5">
                                                        <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded-sm"
                                                            title={c.customer_wish.summary || ""}
                                                            data-testid={`fin-wish-${c.contract_number}`}>
                                                            Wunsch: {c.customer_wish.label}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 font-mono-num text-xs" onClick={(e) => e.stopPropagation()}>
                                                {c.customer?.phone ? <a href={`tel:${c.customer.phone}`} className="text-blue-700 hover:underline">{c.customer.phone}</a> : "—"}
                                            </td>
                                            <td className="px-3 py-2 text-xs">{`${c.vehicle?.manufacturer || ""} ${c.vehicle?.model || ""}`.trim() || "—"}
                                                <div className="text-[10px] text-gray-400 font-mono-num">{c.vehicle?.vin || ""}{c.vehicle?.mileage ? ` · ${Number(c.vehicle.mileage).toLocaleString("de-DE")} km` : ""}</div>
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono-num">{fmtEur(c.rate)}</td>
                                            <td className="px-3 py-2 text-right font-mono-num">
                                                {Number(c.balloon) > 0 ? (
                                                    <span title="Ballon-/Schlussrate"><span className="text-[9px] uppercase text-amber-700 font-bold mr-1">Ballon</span>{fmtEur(c.balloon)}</span>
                                                ) : Number(c.saldo) ? (
                                                    <span title="Offener Restsaldo"><span className="text-[9px] uppercase text-gray-400 font-bold mr-1">Saldo</span>{fmtEur(c.saldo)}</span>
                                                ) : Number(c.amount) ? (
                                                    <span title="Finanzierungssumme (keine Schlussrate)"><span className="text-[9px] uppercase text-gray-400 font-bold mr-1">Summe</span>{fmtEur(c.amount)}</span>
                                                ) : "—"}
                                            </td>
                                            <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                                {c.lead_id ? (
                                                    <div className="space-y-1" data-testid={`fin-lead-cell-${c.contract_number}`}>
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <Link to="/dealer/leads" className="inline-flex items-center gap-1 text-xs font-bold text-green-700 underline" data-testid={`fin-lead-link-${c.contract_number}`}>
                                                                <CheckCircle2 className="w-3.5 h-3.5" /> {LEAD_STATUS_LABELS[lead?.status] || "Lead"}
                                                            </Link>
                                                            {lead?.follow_up_date && (
                                                                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200" data-testid={`fin-followup-${c.contract_number}`}>
                                                                    <CalendarClock className="w-3 h-3" /> WV {fmtDate(lead.follow_up_date)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <SellerSelect
                                                            employees={employees}
                                                            value={lead?.assigned_seller_id || ""}
                                                            disabled={rowBusy === c.id}
                                                            onChange={(v) => reassignSeller(c, v)}
                                                            className="text-xs rounded-sm border border-gray-300 bg-white px-2 py-1 max-w-[170px] focus:border-[#111827] focus:outline-none disabled:opacity-60"
                                                            placeholder="— kein Verkäufer —"
                                                            testid={`fin-lead-seller-${c.contract_number}`}
                                                        />
                                                        {lead?.assigned_seller_id && (
                                                            <AssignNoteInput lead={lead} onSave={(note) => saveAssignNote(c, note)}
                                                                testid={`fin-assign-note-${c.contract_number}`} />
                                                        )}
                                                        {/* Anruf-Ergebnis mit 1 Klick */}
                                                        {lead?.status !== "lost" && lead?.status !== "won" && (
                                                            <div className="flex items-center gap-1" data-testid={`fin-quick-result-${c.contract_number}`}>
                                                                <button type="button" disabled={rowBusy === c.id} onClick={() => quickResult(c, "interest")}
                                                                    title="Erreicht — Interesse"
                                                                    className="p-1 rounded-sm border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-40"
                                                                    data-testid={`fin-result-interest-${c.contract_number}`}>
                                                                    <ThumbsUp className="w-3.5 h-3.5" />
                                                                </button>
                                                                <button type="button" disabled={rowBusy === c.id} onClick={() => quickResult(c, "no_reach")}
                                                                    title="Nicht erreicht — Wiedervorlage morgen"
                                                                    className="p-1 rounded-sm border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-40"
                                                                    data-testid={`fin-result-noreach-${c.contract_number}`}>
                                                                    <PhoneMissed className="w-3.5 h-3.5" />
                                                                </button>
                                                                <button type="button" disabled={rowBusy === c.id} onClick={() => quickResult(c, "no_interest")}
                                                                    title="Kein Interesse — Lead schließen"
                                                                    className="p-1 rounded-sm border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-40"
                                                                    data-testid={`fin-result-nointerest-${c.contract_number}`}>
                                                                    <ThumbsDown className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="space-y-1" data-testid={`fin-createlead-cell-${c.contract_number}`}>
                                                        <SellerSelect
                                                            employees={employees}
                                                            value=""
                                                            disabled={rowBusy === c.id}
                                                            onChange={(v) => v && createLead(c, v)}
                                                            className="text-xs rounded-sm border border-gray-300 bg-white px-2 py-1 max-w-[170px] focus:border-[#111827] focus:outline-none disabled:opacity-60"
                                                            placeholder="— Verkäufer zuweisen —"
                                                            testid={`fin-seller-select-${c.contract_number}`}
                                                        />
                                                        <button type="button" onClick={() => createLead(c)} disabled={rowBusy === c.id}
                                                            className="inline-flex items-center gap-1 text-xs font-bold bg-[#111827] text-white rounded-sm px-2 py-1 hover:bg-black disabled:opacity-50"
                                                            data-testid={`fin-create-lead-${c.contract_number}`}>
                                                            {rowBusy === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                                                            Als Lead übernehmen
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-1 py-2 text-right align-top">
                                                <button type="button"
                                                    onClick={(e) => { e.stopPropagation(); deleteContract(c); }}
                                                    disabled={rowBusy === c.id}
                                                    className="text-gray-300 hover:text-red-600 p-1"
                                                    title="Vorgang löschen"
                                                    data-testid={`fin-delete-${c.contract_number}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Detail-Ansicht (Klick auf Zeile) — schließt NICHT bei Außenklick */}
            <Dialog open={!!detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }}>
                <DialogContent
                    className="max-w-2xl rounded-sm p-0 overflow-hidden"
                    onInteractOutside={(e) => e.preventDefault()}
                    data-testid="fin-detail-modal">
                    {(() => {
                        const c = items.find((x) => x.id === detailId);
                        if (!c) return null;
                        const d = daysUntil(c.end_date);
                        const lead = c.lead;
                        const Row = ({ l, v }) => (v || v === 0) && String(v).trim() !== "" ? (
                            <div className="flex justify-between gap-4 py-1.5 border-b border-gray-100 text-sm">
                                <span className="text-gray-500 shrink-0">{l}</span>
                                <span className="font-medium text-right break-all">{v}</span>
                            </div>
                        ) : null;
                        return (
                            <div>
                                <DialogHeader className="bg-[#111827] text-white px-5 py-4">
                                    <DialogTitle className="flex items-center justify-between gap-3 text-base">
                                        <span className="truncate">{c.customer?.name || "Vorgang"} — Vertrag {c.contract_number}</span>
                                        <span className="bg-white rounded-sm px-2 py-1 shrink-0"><BankLogo bank={c.bank} className="h-6" /></span>
                                    </DialogTitle>
                                    <div className="text-xs text-gray-300">
                                        Vertragsende <b className={d >= 0 && d <= 30 ? "text-red-400" : "text-[#FFCC00]"}>{fmtDate(c.end_date)}</b>
                                        {d >= 0 ? ` — in ${d} Tagen` : ` — seit ${Math.abs(d)} Tagen abgelaufen`}
                                    </div>
                                </DialogHeader>
                                <div className="px-5 py-4 grid md:grid-cols-2 gap-x-8 gap-y-1 max-h-[55vh] overflow-y-auto">
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Kunde</div>
                                        <Row l="Name" v={`${c.customer?.salutation || ""} ${c.customer?.name || ""}`.trim()} />
                                        <Row l="Straße" v={c.customer?.street} />
                                        <Row l="Ort" v={`${c.customer?.plz || ""} ${c.customer?.city || ""}`.trim()} />
                                        <Row l="Telefon" v={c.customer?.phone ? <a href={`tel:${c.customer.phone}`} className="text-blue-700 underline">{c.customer.phone}</a> : null} />
                                        <Row l="E-Mail" v={c.customer?.email ? <a href={`mailto:${c.customer.email}`} className="text-blue-700 underline">{c.customer.email}</a> : null} />
                                        <Row l="Geburtsdatum" v={fmtDate(c.customer?.birthdate) !== "—" ? fmtDate(c.customer?.birthdate) : null} />
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-3 mb-1">Fahrzeug</div>
                                        <Row l="Modell" v={`${c.vehicle?.manufacturer || ""} ${c.vehicle?.model || ""}`.trim()} />
                                        <Row l="FIN" v={c.vehicle?.vin} />
                                        <Row l="Kennzeichen" v={c.vehicle?.plate} />
                                        <Row l="Erstzulassung" v={fmtDate(c.vehicle?.first_registration) !== "—" ? fmtDate(c.vehicle?.first_registration) : null} />
                                        <Row l="km-Stand" v={c.vehicle?.mileage ? `${Number(c.vehicle.mileage).toLocaleString("de-DE")} km` : null} />
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Vertrag</div>
                                        <Row l="Bank" v={c.bank} />
                                        <Row l="Vertragsnr." v={c.contract_number} />
                                        <Row l="Rate" v={c.rate ? fmtEur(c.rate) : null} />
                                        <Row l="Ballonrate" v={c.balloon ? fmtEur(c.balloon) : null} />
                                        <Row l="Saldo" v={c.saldo ? fmtEur(c.saldo) : null} />
                                        <Row l="Kaufpreis / Betrag" v={c.amount ? fmtEur(c.amount) : null} />
                                        <Row l="Verkäufer (lt. Bank-Liste)" v={c.seller} />
                                        {c.customer_wish?.label && (
                                            <>
                                                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-500 mt-3 mb-1">Kundenwunsch (KI-erkannt aus Antwort)</div>
                                                <div className="bg-violet-50 border border-violet-200 rounded-sm px-3 py-2 text-sm" data-testid="fin-detail-wish">
                                                    <div className="font-bold text-violet-900">{c.customer_wish.label}</div>
                                                    {c.customer_wish.summary && <div className="text-xs text-violet-800 mt-0.5">{c.customer_wish.summary}</div>}
                                                    <div className="text-[10px] text-violet-500 mt-1">
                                                        Quelle: {c.customer_wish.source === "email" ? "E-Mail" : c.customer_wish.source === "whatsapp" ? "WhatsApp" : "SMS"}
                                                        {c.customer_wish.detected_at ? ` · ${fmtDate(c.customer_wish.detected_at.slice(0, 10))}` : ""}
                                                    </div>
                                                    {/* Jul-2026 (User-Wunsch) — Wunsch-Weiterleitung als Verkäufer-Aufgabe */}
                                                    <div className="mt-2">
                                                        {c.wish_task?.task_id ? (
                                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700" data-testid="fin-wish-task-done">
                                                                <CheckCircle2 className="w-3.5 h-3.5" /> Aufgabe erstellt — {c.wish_task.assignee_name}
                                                            </span>
                                                        ) : (
                                                            <Button size="sm" onClick={() => createWishTask(c.id)} disabled={wishTaskBusy}
                                                                className="h-7 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-[11px] font-bold"
                                                                data-testid="fin-wish-task-btn">
                                                                {wishTaskBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><UserPlus className="w-3.5 h-3.5 mr-1" /> Aufgabe für Verkäufer erstellen</>}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-3 mb-1">Lead / Verkäufer-Zuordnung</div>
                                        {c.lead_id ? (
                                            <div className="space-y-2 text-sm">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Link to={`/dealer/leads?lead=${c.lead_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-green-700 underline" data-testid="fin-detail-lead-link">
                                                        <CheckCircle2 className="w-3.5 h-3.5" /> Lead: {LEAD_STATUS_LABELS[lead?.status] || "Offen"} — öffnen
                                                    </Link>
                                                    {lead?.follow_up_date && (
                                                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200">
                                                            <CalendarClock className="w-3 h-3" /> WV {fmtDate(lead.follow_up_date)}
                                                        </span>
                                                    )}
                                                </div>
                                                <SellerSelect
                                                    employees={employees}
                                                    value={lead?.assigned_seller_id || ""}
                                                    disabled={rowBusy === c.id}
                                                    onChange={(v) => reassignSeller(c, v)}
                                                    className="w-full text-sm rounded-sm border border-gray-300 bg-white px-2 py-1.5 focus:border-[#111827] focus:outline-none disabled:opacity-60"
                                                    placeholder="— kein Verkäufer —"
                                                    testid="fin-detail-lead-seller"
                                                />
                                                {lead?.assigned_seller_id && (
                                                    <AssignNoteInput lead={lead} onSave={(note) => saveAssignNote(c, note)} testid="fin-detail-assign-note" />
                                                )}
                                                <div className="text-[11px] text-gray-500">Der zugewiesene Verkäufer sieht den Vorgang in seinem Lead-Eingang („Meine Leads").</div>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <SellerSelect
                                                    employees={employees}
                                                    value=""
                                                    disabled={rowBusy === c.id}
                                                    onChange={(v) => v && createLead(c, v)}
                                                    className="w-full text-sm rounded-sm border border-gray-300 bg-white px-2 py-1.5 focus:border-[#111827] focus:outline-none disabled:opacity-60"
                                                    placeholder="— Verkäufer zuweisen (legt Lead sofort an) —"
                                                    testid="fin-detail-seller-select"
                                                />
                                                <button type="button" onClick={() => createLead(c)} disabled={rowBusy === c.id}
                                                    className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-bold bg-[#111827] text-white rounded-sm px-3 py-2 hover:bg-black disabled:opacity-50"
                                                    data-testid="fin-detail-create-lead">
                                                    {rowBusy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                                    Als Lead übernehmen (ohne Verkäufer)
                                                </button>
                                                <div className="text-[11px] text-gray-500">Mit Verkäufer-Auswahl landet der Vorgang direkt im Lead-Eingang des Verkäufers.</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
            <FinRunoutSettingsDialog open={runoutOpen} onClose={() => setRunoutOpen(false)}
                settings={settings} onSaved={() => load()} />
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
                <DialogContent className="max-w-xl" data-testid="fin-import-dialog">
                    <DialogHeader>
                        <DialogTitle>Bank-Liste importieren</DialogTitle>
                    </DialogHeader>
                    <BankImportCard onDone={() => { setImportOpen(false); load(); }} />
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Kurzinfo für den zugewiesenen Verkäufer (User-Wunsch Jul-2026) — Speichern bei Enter/Verlassen.
function AssignNoteInput({ lead, onSave, testid }) {
    const [val, setVal] = useState(lead?.assignment_note || "");
    useEffect(() => { setVal(lead?.assignment_note || ""); }, [lead?.assignment_note]);
    const commit = () => {
        const next = val.trim();
        if ((lead?.assignment_note || "") === next) return;
        onSave(next);
    };
    return (
        <input value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            placeholder="Kurzinfo für Mitarbeiter…" maxLength={300}
            className="block w-full max-w-[170px] text-xs rounded-sm border border-amber-300 bg-amber-50/60 px-2 py-1 focus:border-[#111827] focus:outline-none"
            title="Kurze Info, die der zugewiesene Verkäufer deutlich am Lead sieht"
            data-testid={testid} />
    );
}
